import React, { useContext, useState } from 'react'
import { Button, Modal, ListMenu, ListMenuButton, NotificationManagerContext,
   Popover, Text, View } from 'oio-react'
import { Route, useHistory, useParams, useRouteMatch } from 'react-router-dom'
import contentTypeThemes from 'config/constants/contentTypeThemes'
import { settingsContentUrl } from 'config/constants/urls'
import InitiativeCreate from 'src/apps/Initiative/apps/Create'
import InitiativeListItemRow from 'src/apps/AdminWorkspace/components/InitiativeListItemRow'
import InitiativeDeleteConfirm from 'src/components/Initiative/DeleteConfirm'
import ContentListSearchBox from 'src/components/Settings/ContentListSearchBox'
import { useInitiativeList, useOrganization, useUpdateInitiative } from 'src/graphql/hooks'

const SettingsContentInitiativeTypeListing = () => {
   const history = useHistory()
   const { initiativeStatus, initiativeTypeId } = useParams()
   const match = useRouteMatch()

   const { showNotification } = useContext(NotificationManagerContext)
   const [createModalIsOpen, setCreateModalIsOpen] = useState(true)
   const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false)
   const [activeSearchValue, setActiveSearchValue] = useState('')

   const [popoverConfig, setPopoverConfig] = useState({
      anchorElement: null,
      focusedValue: null,
      isOpen: false
   })

   const { organization } = useOrganization()
   const initiativeType = organization.initiativeTypes.find(t => t.id === initiativeTypeId)
   const contentInitiativeTypeUrl = `${settingsContentUrl}/initiativeType/${initiativeTypeId}`
   const Icon = contentTypeThemes[initiativeType.class]?.icon

   const initiativeListFilter = {
      archived: 'exclude',
      drafts: 'include',
      search: activeSearchValue,
      typeIds: initiativeTypeId,
      limit: 25
   }

   if (initiativeStatus === 'archived') {
      initiativeListFilter.archived = 'only'
   }

   const { initiativeList, fetchMore, loading, refetch } = useInitiativeList(initiativeListFilter)
   const { updateInitiative } = useUpdateInitiative()

   const handleUpdateInitiative = async (values) => {
      try {
         await updateInitiative({ id: popoverConfig.focusedValue.id }, values)
         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })
         refetch()
      } catch (err) {
         showNotification({
            message: 'An error occurred while saving changes',
            title: 'Error',
            type: 'error'
         })
      }
   }

   const handleLoadMore = () => {
      fetchMore({
         variables: {
            start: initiativeList.listInfo.nextCursor
         },
         updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
               return prevResult
            }

            return {
               ...prevResult,
               initiativeList: {
                  ...prevResult.initiativeList,
                  items: [
                     ...prevResult.initiativeList.items,
                     ...fetchMoreResult.initiativeList.items
                  ],
                  listInfo: fetchMoreResult.initiativeList.listInfo
               }
            }
         }
      })
   }

   const handleClosePopover = () => {
      setPopoverConfig(config => ({
         ...config,
         isOpen: false,
         anchorElement: null
      }))
   }

   return (
      <View>
         <View
            width="100%"
            borderRadius="3px"
            backgroundColor="#fff">
            <ContentListSearchBox
               onSubmit={setActiveSearchValue}
            />
            {initiativeList.items.length === 0 && (
               <View
                  float="left"
                  textAlign="center"
                  backgroundColor="#fff"
                  width="100%"
                  padding="30px">
                  <Text size="1.5">
                     {`No ${initiativeType.namePlural} to show here`}
                  </Text>
               </View>
            )}
            {initiativeList.items.map(initiative => (
               <InitiativeListItemRow
                  key={initiative.id}
                  editUrl={`/initiatives/${initiative.slug}/settings`}
                  dateLastUpdated={initiative.dateLastUpdated}
                  name={initiative.name}
                  privacy={initiative.privacy}
                  onOptionsButtonClick={(e) => {
                     setPopoverConfig({
                        isOpen: true,
                        anchorElement: e.currentTarget,
                        focusedValue: initiative
                     })
                  }}
                  icon={Icon && (
                     <View
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="30px"
                        height="30px"
                        borderRadius="3px"
                        backgroundColor="#ddd"
                        marginRight="12px">
                        <Icon
                           width="14px"
                           height="14px"
                           color="#fff"
                           strokeWidth="2px"
                        />
                     </View>
                  )}
               />
            ))}
            {initiativeList.listInfo.hasNext && (
               <View
                  float="left"
                  width="100%"
                  display="flex"
                  justifyContent="center"
                  padding="12px 0px">
                  <Button
                     color="#000"
                     textColor="#fff"
                     size="xs"
                     onClick={handleLoadMore}
                     name="Load More"
                     mode={loading ? 'loading' : 'normal'}
                     rounded
                  />
               </View>
            )}
            {/* Initiative List Item Popover */}
            <Popover
               anchorElement={popoverConfig.anchorElement}
               anchorOriginHorizontal="right"
               anchorOriginVertical="top"
               backgroundColor="#fff"
               borderRadius="6px"
               width="150px"
               onBodyClick={handleClosePopover}
               open={popoverConfig.isOpen}>
               <View
                  float="left"
                  width="100%"
                  backgroundColor="#fff"
                  borderRadius="6px"
                  boxShadow="3px 3px 12px rgba(0,0,0,0.2)">
                  {popoverConfig.focusedValue && (
                     <>
                        <ListMenu buttonSize="md" buttonTextColor="#333">
                           <ListMenuButton
                              name="Edit"
                              linkTo={`/initiatives/${popoverConfig.focusedValue.slug}/settings`}
                           />
                        </ListMenu>
                        <View float="left" width="100%" borderTop="1px solid #e5e5e5">
                           <ListMenu buttonSize="sm">
                              {initiativeStatus === 'archived' && (
                                 <ListMenuButton
                                    onClick={() => handleUpdateInitiative({
                                       archived: false
                                    })}
                                    name="Unarchive"
                                 />
                              )}
                              {initiativeStatus !== 'archived' && (
                                 <ListMenuButton
                                    onClick={() => handleUpdateInitiative({
                                       archived: true
                                    })}
                                    name="Archive"
                                 />
                              )}
                              <ListMenuButton
                                 onClick={() => setDeleteModalIsOpen(true)}
                                 name="Delete"
                              />
                           </ListMenu>
                        </View>
                     </>
                  )}
               </View>
            </Popover>
         </View>
         {/* Create Initiative Modal */}
         <Route
            path={`${match.url}/create`}
            render={() => (
               <Modal
                  borderRadius="6px"
                  width="300px"
                  onCloseComplete={() => {
                     history.push(match.url)
                     setCreateModalIsOpen(true)
                  }}
                  onCloseTrigger={() => setCreateModalIsOpen(false)}
                  open={createModalIsOpen}>
                  <InitiativeCreate
                     initiativeTypeId={initiativeTypeId}
                     onCancelButtonClick={() => setCreateModalIsOpen(false)}
                     onCloseTrigger={() => setCreateModalIsOpen(false)}
                     onCreate={(initiative) => {
                        setCreateModalIsOpen(true)
                        // TODO: This Redirect URL will be a settings-based route to the actual
                        // initiative settings (done in initiative-settings branch)
                        history.push(`${contentInitiativeTypeUrl}/active`)
                     }}
                  />
               </Modal>
            )}
         />
         {/* Delete Initiative Confirmation Modal */}
         {!!popoverConfig.focusedValue && (
            <Modal
               borderRadius="6px"
               width="90%[a-b] 300px[c-f]"
               onCloseTrigger={() => setDeleteModalIsOpen(false)}
               open={deleteModalIsOpen}>
               <InitiativeDeleteConfirm
                  initiativeId={popoverConfig.focusedValue.id}
                  initiativeName={popoverConfig.focusedValue.name}
                  onCancelButtonClick={() => setDeleteModalIsOpen(false)}
                  onDelete={() => setDeleteModalIsOpen(false)}
               />
            </Modal>
         )}
      </View>
   )
}

export default SettingsContentInitiativeTypeListing
